<nav
  class="side-rail-nav"
  aria-label="Main navigation"
  [class.is-collapsed]="isCollapsed"
  [style.--rail-offset.px]="offsetTop"
>
  <div class="side-rail-nav__brand" [class.is-collapsed]="isCollapsed">
    <a class="side-rail-nav__brand-link" routerLink="/" aria-label="Parole Hearing Management System home">
      <span class="side-rail-nav__brand-mark">
        <app-phms-brand-mark
          class="side-rail-nav__brand-logo"
        ></app-phms-brand-mark>
      </span>
      @if (!isCollapsed) {
        <span class="side-rail-nav__brand-wordmark">
          <span>Parole</span>
          <span>Hearing</span>
          <span>Management</span>
          <span>System</span>
        </span>
      }
    </a>
  </div>

  <div class="side-rail-nav__toggle-row">
    <button
      type="button"
      class="side-rail-nav__mode-toggle"
      (click)="toggleMode()"
      [attr.aria-label]="
        isCollapsed ? 'Expand navigation' : 'Collapse navigation'
      "
      [attr.aria-expanded]="!isCollapsed"
      [attr.title]="isCollapsed ? 'Expand navigation' : 'Collapse navigation'"
    >
      <mat-icon>{{
        isCollapsed
          ? "keyboard_double_arrow_right"
          : "keyboard_double_arrow_left"
      }}</mat-icon>
    </button>
  </div>

  <div class="side-rail-nav__divider"></div>

  @if (!isCollapsed) {
    <div class="side-rail-nav__section-label">NAVIGATION</div>
  }

  <div class="side-rail-nav__content">
    <ng-container
      [ngTemplateOutlet]="itemList"
      [ngTemplateOutletContext]="{ items: items, depth: 0 }"
    ></ng-container>
  </div>
</nav>

<ng-template #itemList let-items="items" let-depth="depth">
  @for (
    item of items;
    track item.extraProps?.nodeCode ?? item.route ?? item.displayName
  ) {
    <div
      class="side-rail-nav__item-container"
      [class.is-collapsed]="isCollapsed"
      [class.is-active]="item.isActive"
      [class.is-parent]="item.hasChildren"
      [class.is-flyout-menu]="isFlyoutPresentation(item)"
      data-testid="side-nav-item"
      [attr.data-nav-item]="item.displayName"
      [attr.tabindex]="isFlyoutPresentation(item) ? -1 : null"
      (mouseenter)="onItemEnter(item, $event, depth)"
      (mouseleave)="onItemLeave(item, $event)"
      (focusin)="onItemFocusIn(item, $event, depth)"
      (focusout)="onItemFocusOut(item, $event)"
      (keydown.escape)="onItemEscape(item, $event)"
    >
      <div
        class="side-rail-nav__item"
        [style.padding-left.px]="isCollapsed ? 0 : depth * 12"
      >
        @if (item.hasChildren) {
          <button
            type="button"
            class="side-rail-nav__button"
            (click)="onItemClick(item, $event)"
            [attr.data-testid]="item.testId ?? null"
            [attr.aria-haspopup]="isFlyoutPresentation(item) ? 'menu' : null"
            [attr.aria-expanded]="
              isFlyoutPresentation(item) ? hoveredItem === item : null
            "
            [attr.aria-label]="labelFor(item)"
          >
            @if (item.iconName) {
              <ng-container
                [ngTemplateOutlet]="navigationIcon"
                [ngTemplateOutletContext]="{ iconName: item.iconName }"
              ></ng-container>
            }
            @if (!isCollapsed) {
              <span class="side-rail-nav__label">{{ labelFor(item) }}</span>
              @if (isFlyoutPresentation(item)) {
                <mat-icon class="side-rail-nav__chevron"
                  >chevron_right</mat-icon
                >
              } @else {
                <mat-icon class="side-rail-nav__chevron">
                  {{ item.expanded ? "expand_less" : "expand_more" }}
                </mat-icon>
              }
            }
          </button>
        } @else {
          @if (isRouterItem(item)) {
            <a
              class="side-rail-nav__link"
              [attr.aria-label]="labelFor(item)"
              [routerLink]="routerLinkFor(item)"
              (click)="onItemClick(item, $event)"
              [attr.data-testid]="item.testId ?? null"
            >
              @if (item.iconName) {
                <ng-container
                  [ngTemplateOutlet]="navigationIcon"
                  [ngTemplateOutletContext]="{ iconName: item.iconName }"
                ></ng-container>
              }
              @if (!isCollapsed) {
                <span class="side-rail-nav__label">{{ labelFor(item) }}</span>
              }
            </a>
          } @else {
            <a
              class="side-rail-nav__link"
              [attr.aria-label]="labelFor(item)"
              [attr.href]="getItemHref(item)"
              [attr.target]="item.urlTarget || '_blank'"
              (click)="onItemClick(item, $event)"
              [attr.data-testid]="item.testId ?? null"
            >
              @if (item.iconName) {
                <ng-container
                  [ngTemplateOutlet]="navigationIcon"
                  [ngTemplateOutletContext]="{ iconName: item.iconName }"
                ></ng-container>
              }
              @if (!isCollapsed) {
                <span class="side-rail-nav__label">{{ labelFor(item) }}</span>
              }
            </a>
          }
        }
      </div>

      @if (shouldShowFlyout(item, depth) && hoveredItem === item) {
        <div
          class="side-rail-nav__flyout"
          [class.is-leaf]="!item.hasChildren"
          [class.is-menu]="isFlyoutPresentation(item)"
          [ngStyle]="flyoutStyle"
          (mouseleave)="onFlyoutLeave(item, $event)"
        >
          <div class="side-rail-nav__flyout-header">
            @if (item.iconName) {
              <ng-container
                [ngTemplateOutlet]="navigationIcon"
                [ngTemplateOutletContext]="{ iconName: item.iconName }"
              ></ng-container>
            }
            <span class="side-rail-nav__label">{{ labelFor(item) }}</span>
          </div>

          @if (item.hasChildren) {
            <div
              class="side-rail-nav__flyout-content"
              [attr.role]="isFlyoutPresentation(item) ? 'menu' : null"
              [attr.aria-label]="
                isFlyoutPresentation(item) ? labelFor(item) : null
              "
            >
              <ng-container
                [ngTemplateOutlet]="flyoutList"
                [ngTemplateOutletContext]="{
                  items: item.children ?? [],
                  depth: 0,
                  menu: isFlyoutPresentation(item),
                }"
              ></ng-container>
            </div>
          }
        </div>
      }
    </div>

    @if (
      item.hasChildren &&
      !isCollapsed &&
      item.expanded &&
      !isFlyoutPresentation(item)
    ) {
      <div class="side-rail-nav__children">
        <ng-container
          [ngTemplateOutlet]="itemList"
          [ngTemplateOutletContext]="{
            items: item.children ?? [],
            depth: depth + 1,
          }"
        ></ng-container>
      </div>
    }
  }
</ng-template>

<ng-template #flyoutList let-items="items" let-depth="depth" let-menu="menu">
  @for (
    item of items;
    track item.extraProps?.nodeCode ?? item.route ?? item.displayName
  ) {
    <div class="side-rail-nav__flyout-item" [class.is-active]="item.isActive">
      @if (item.hasChildren) {
        <div class="side-rail-nav__flyout-group">
          <span class="side-rail-nav__flyout-title">{{ labelFor(item) }}</span>
        </div>
      } @else {
        @if (isRouterItem(item)) {
          <a
            class="side-rail-nav__flyout-link"
            [class.is-active]="item.isActive || (menu && isCurrentRoute(item))"
            [routerLink]="routerLinkFor(item)"
            (click)="onItemClick(item, $event)"
            [attr.role]="menu ? 'menuitem' : null"
            [attr.aria-current]="menu && isCurrentRoute(item) ? 'page' : null"
            [attr.data-testid]="item.testId ?? null"
          >
            @if (item.iconName) {
              <ng-container
                [ngTemplateOutlet]="navigationIcon"
                [ngTemplateOutletContext]="{ iconName: item.iconName }"
              ></ng-container>
            }
            <span class="side-rail-nav__label">{{ labelFor(item) }}</span>
          </a>
        } @else {
          <a
            class="side-rail-nav__flyout-link"
            [class.is-active]="item.isActive"
            [attr.href]="getItemHref(item)"
            [attr.target]="item.urlTarget || '_blank'"
            (click)="onItemClick(item, $event)"
            [attr.role]="menu ? 'menuitem' : null"
            [attr.data-testid]="item.testId ?? null"
          >
            @if (item.iconName) {
              <ng-container
                [ngTemplateOutlet]="navigationIcon"
                [ngTemplateOutletContext]="{ iconName: item.iconName }"
              ></ng-container>
            }
            <span class="side-rail-nav__label">{{ labelFor(item) }}</span>
          </a>
        }
      }
    </div>

    @if (item.hasChildren) {
      <div class="side-rail-nav__flyout-children">
        <ng-container
          [ngTemplateOutlet]="flyoutList"
          [ngTemplateOutletContext]="{
            items: item.children ?? [],
            depth: depth + 1,
            menu: menu,
          }"
        ></ng-container>
      </div>
    }
  }
</ng-template>

<ng-template #navigationIcon let-iconName="iconName">
  @if (primeIcon(iconName); as iconClass) {
    <i class="side-rail-nav__icon" [ngClass]="iconClass" aria-hidden="true"></i>
  } @else {
    <mat-icon class="side-rail-nav__icon" aria-hidden="true">{{
      materialIcon(iconName)
    }}</mat-icon>
  }
</ng-template>
